import { STORAGE, storageGet, storageSet } from '../utils/storageKeys.js';

const MAX_SCORE = 10_000_000;
const GRADE_ORDER = { S: 3, A: 2, B: 1, C: 0 };

/**
 * Best score per (song, difficulty), kept locally so the catalog rank badges
 * work offline. Merged with the server account scores when online (max wins).
 */
export default class SongBestStore {
  constructor() {
    this.map = this._load();
  }

  _load() {
    const raw = storageGet(STORAGE.SONG_BEST);
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
      const clean = {};
      for (const [key, value] of Object.entries(parsed)) {
        const entry = sanitize(value);
        if (entry && /^[\w.-]+\|(easy|normal|hard)$/.test(key)) clean[key] = entry;
      }
      return clean;
    } catch {
      return {};
    }
  }

  _save() {
    storageSet(STORAGE.SONG_BEST, JSON.stringify(this.map));
  }

  static key(songId, difficulty) {
    return `${songId}|${difficulty}`;
  }

  get(songId, difficulty) {
    return this.map[SongBestStore.key(songId, difficulty)] ?? null;
  }

  /** Records a finished run; keeps only the best score per song/difficulty. */
  submit({ songId, difficulty, score, grade }) {
    const entry = sanitize({ score, grade });
    if (!entry || !songId || !['easy', 'normal', 'hard'].includes(difficulty)) return;
    const key = SongBestStore.key(String(songId), difficulty);
    const prev = this.map[key];
    if (!prev || entry.score > prev.score) {
      this.map[key] = entry;
      this._save();
    }
  }

  /**
   * Merge server rows ({ songId, difficulty, score, grade }) into a fresh map
   * (server AND local, max score wins). Returns { "songId|difficulty": entry }.
   */
  mergedWith(serverScores) {
    const merged = { ...this.map };
    for (const row of serverScores || []) {
      const entry = sanitize(row);
      if (!entry || !row.songId) continue;
      const key = SongBestStore.key(String(row.songId), row.difficulty);
      if (!merged[key] || entry.score > merged[key].score) merged[key] = entry;
    }
    return merged;
  }
}

function sanitize(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const score = Math.floor(Number(raw.score));
  if (!Number.isFinite(score) || score < 0 || score > MAX_SCORE) return null;
  const grade = String(raw.grade ?? 'C').slice(0, 1);
  if (!(grade in GRADE_ORDER)) return null;
  return { score, grade };
}
